import { useState, useEffect, useMemo } from "react";
import "../../assets/styles/industry.css";
import { IoBagAdd } from "react-icons/io5";
import Modal from "../../components/common/modal";
import { MdModeEdit, MdDelete } from "react-icons/md";
import { HiDotsVertical } from "react-icons/hi";
import Toggle from "../../components/common/toggle";
import { useViewCategory } from "../../hooks/categorys/category.showall";
import { useCategoryUpdate } from "../../hooks/categorys/category.update";
import CategoryModal from "../../components/modal/category.modal";
import { useRemoveCategory } from "../../hooks/categorys/category.delete";
import toast from "react-hot-toast";
import Pagination from "../../components/common/pagination";
import { useRef } from "react";
import Swal from "sweetalert2";


const Categories = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReadMoreModalOpen, setIsReadMoreModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [imageBlobUrls, setImageBlobUrls] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isShowingAll, setIsShowingAll] = useState(false);
  const [totalItemsCount, setTotalItemsCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");




  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    description: "",
    image: null,
    catlogs: [],
  });

  const { data: categories, isLoading, isError } = useViewCategory(
    isShowingAll ? undefined : currentPage,
    isShowingAll ? (totalItemsCount || 10000) : pageSize,
    debouncedSearchTerm,
  );

  const categoryUpdateMutation = useCategoryUpdate();
  const categoryDeleteMutation = useRemoveCategory();

  useEffect(() => {
    if (categories?.data?.pagination?.totalDocs) {
      setTotalItemsCount(categories.data.pagination.totalDocs);
    } else if (categories?.data?.pagination?.totalItems) {
      setTotalItemsCount(categories.data.pagination.totalItems);
    } else if (categories?.data?.data?.length) {
      setTotalItemsCount(categories.data.data.length);
    }
  }, [categories]);

  const rawCategoriesList = categories?.data?.data || [];
  const serverPagination = categories?.data?.pagination;
  const isServerPaginated = Boolean(serverPagination?.totalPages);

  const totalItems = isServerPaginated
    ? serverPagination?.totalDocs || serverPagination?.totalItems || rawCategoriesList.length
    : rawCategoriesList.length;

  const totalPages = isServerPaginated
    ? serverPagination?.totalPages || 1
    : Math.max(1, Math.ceil(rawCategoriesList.length / pageSize));

  const categoriesList = useMemo(
    () =>
      isServerPaginated
        ? rawCategoriesList
        : rawCategoriesList.slice(
            (currentPage - 1) * pageSize,
            currentPage * pageSize,
          ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [categories, isServerPaginated, currentPage, pageSize]
  );

  const filteredCategories = useMemo(() => {
    const trimmed = searchTerm.trim().toLowerCase();
    if (!trimmed) return categoriesList;
    return categoriesList.filter((category) => {
      const title = (category?.title || category?.name || "").toLowerCase();
      const slug = (category?.slug || "").toLowerCase();
      const description = (category?.description || "").toLowerCase();
      return (
        title.includes(trimmed) ||
        slug.includes(trimmed) ||
        description.includes(trimmed)
      );
    });
  }, [categoriesList, searchTerm]);

  const activePage = isServerPaginated
    ? serverPagination?.currentPage || currentPage
    : currentPage;

useEffect(() => {
  let isMounted = true;
  const newBlobUrls = {};

  const loadImages = async () => {
    for (const category of categoriesList) {
      if (category.image) {
        const imageUrl = `${process.env.REACT_APP_BASE_IMAGE}${category.image}`;
        try {
          const response = await fetch(imageUrl, {
            headers: {
              "ngrok-skip-browser-warning": "69420",
            },
          });

          if (response.ok) {
            const blob = await response.blob();
            newBlobUrls[category.id] = URL.createObjectURL(blob);
          }
        } catch (err) {
          console.error(err);
        }
      }
    }

    if (isMounted) {
      setImageBlobUrls(newBlobUrls);
    }
  };

  if (categoriesList.length) {
    loadImages();
  }

  return () => {
    isMounted = false;
    Object.values(newBlobUrls).forEach((url) =>
      URL.revokeObjectURL(url)
    );
  };
}, [categoriesList]);

useEffect(() => {
  if (categoriesList.length > 0) {
    const initialCheckedMap = {};
    categoriesList.forEach(category => {
      initialCheckedMap[category.id] = category.isActive || false;
    });
    setCheckedMap(initialCheckedMap);
  }
}, [categoriesList]);

useEffect(() => {
  if (currentPage > totalPages) {
    setCurrentPage(totalPages);
  }
}, [currentPage, totalPages]);

useEffect(() => {
  const timer = setTimeout(() => {
    setDebouncedSearchTerm(searchTerm);
  }, 400);

  return () => clearTimeout(timer);
}, [searchTerm]);

useEffect(() => {
  setCurrentPage(1);
}, [debouncedSearchTerm]);




  const [checkedMap, setCheckedMap] = useState({});

  const onToggleChange = async (id, newChecked) => {
    // Update local state immediately for better UX
    setCheckedMap((prev) => ({
      ...prev,
      [id]: newChecked,
    }));

    if (id !== 0) {
      try {
        await categoryUpdateMutation.mutateAsync({
          id: id,
          body: { isActive: Boolean(newChecked) },
        });
        toast.success(`Category ${newChecked ? 'activated' : 'deactivated'} successfully`);
      } catch (error) {
        console.error("Failed to update category status:", error);
        toast.error("Failed to update category status");
        // Revert the change on error
        setCheckedMap((prev) => ({
          ...prev,
          [id]: !newChecked,
        }));
      }
    }
  };

 


  const handleOpenModal = () => {
    setIsModalOpen(true);
    setEditingCategoryId(null);
    setFormData({ title: "", slug: "", description: "", image: null, catlogs: [] });
    setCheckedMap({ 0: true });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCategoryId(null);
    setFormData({ title: "", slug: "", description: "", image: null, catlogs: [] });
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setIsShowingAll(false);
    setCurrentPage(1);
  };

  const handleOpenReadMore = (category) => {
    setSelectedCategory(category);
    setIsReadMoreModalOpen(true);
  };

  const handleCloseReadMore = () => {
    setIsReadMoreModalOpen(false);
    setSelectedCategory(null);
  };

  const handleMenuToggle = (id) => {
    setOpenMenuId(openMenuId === id ? null : id);
  };


  // for implementing close menu outside click

  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleEdit = (category) => {
    setOpenMenuId(null);
    setEditingCategoryId(category.id);
    setIsModalOpen(true);
  };

  const handleDelete = (categoryId) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "This action cannot be undone.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33', 
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
     categoryDeleteMutation.mutate(categoryId);
     toast.success("Category deleted successfully");
      }
    });
    setOpenMenuId(null);
  };


  if (isLoading) {
    return <div>Loading...</div>;
  }

  // if (isError) {
  //   return <div>Error loading categories.</div>;
  // }

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          flexWrap: "wrap",
          marginBottom: "12px",
        }}
      >
        <div style={{ flex: "1 1 600px", minWidth: "220px" }}>
          <input
            type="text"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "100%",
              padding: "8px 10px",
              border: "1px solid #d9d9d9",
              borderRadius: "6px",
              fontSize: "14px",
            }}
          />
        </div>
        <button className="commonButton" onClick={handleOpenModal}>
          <IoBagAdd size={20} color="#ffffff" style={{ marginRight: 8 }} /> Add
          Category
        </button>
      </div>

      <CategoryModal 
        isModalOpen={isModalOpen}
        handleCloseModal={handleCloseModal}
        formData={formData}
        setFormData={setFormData}
        checkedMap={checkedMap}
        onToggleChange={onToggleChange}
        editingCategoryId={editingCategoryId}
      />

      <Modal
        isOpen={isReadMoreModalOpen}
        onClose={handleCloseReadMore}
        title={selectedCategory?.name || "Category Details"}
      >
        <div className="body">
          {selectedCategory?.image && (
            <div style={{ flex: "0 0 40%" }}>
              <img
                src={imageBlobUrls[selectedCategory.id] || selectedCategory.image}
                alt={selectedCategory.name}
                style={{
                  width: "100%",
                  height: "auto",
                  borderRadius: "8px",
                }}
              />
            </div>
          )}
          <div style={{ flex: 1 }}>
            <p
              style={{
                lineHeight: "1.6",
                color: "#333",
                fontSize: "0.95rem",
                textAlign: "left",
                margin: 0,
              }}
            >
              {selectedCategory?.description}
            </p>
          </div>
        </div>
      </Modal>


      <div className="body">
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "12px",
          }}
        >
          <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px" }}>
            Items per page:
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              style={{ padding: "4px 8px" }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={20}>20</option>
            </select>
          </label>
        </div>

        <ul className="cards">
          {filteredCategories.length === 0 ? (
            <li
              style={{
                listStyle: "none",
                width: "100%",
                minHeight: "140px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                padding: "40px 16px",
                color: "#999",
                fontSize: "15px",
              }}
            >
              {searchTerm.trim() ? "No categories found matching your search." : "No categories available."}
            </li>
          ) : (
            filteredCategories.map((category) => (
            <li className="cards__item" key={category.id}>
              <div className="card">
                <div
                  ref={openMenuId === category.id ? menuRef : null}
                  style={{
                    position: "absolute",
                    top: "10px",
                    right: "10px",
                    zIndex: 10,
                  }}
                >
                  <button
                    onClick={() => handleMenuToggle(category.id)}
                    style={{
                      background: "white",
                      border: "none",
                      borderRadius: "8px",
                      padding: "8px",
                      cursor: "pointer",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                  >
                    <HiDotsVertical
                      style={{
                        color: "#333",
                        width: "20px",
                        height: "20px",
                      }}
                    />
                  </button>
                  {openMenuId === category.id && (
                    <div
                      style={{
                        position: "absolute",
                        top: "45px",
                        right: "0",
                        background: "white",
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        minWidth: "140px",
                        overflow: "hidden",
                        zIndex: 20,
                      }}
                    >
                      <button
                        onClick={() => handleEdit(category)}
                        style={{
                          width: "100%",
                          padding: "12px 16px",
                          border: "none",
                          background: "white",
                          cursor: "pointer",
                          textAlign: "left",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          fontSize: "14px",
                          color: "#404040e9",
                          fontWeight: "600",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#f5f5f5";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "white";
                        }}
                      >
                        <MdModeEdit className="icons" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(category.id)}
                        style={{
                          width: "100%",
                          padding: "12px 16px",
                          border: "none",
                          background: "white",
                          cursor: "pointer",
                          textAlign: "left",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          fontSize: "14px",
                          color: "#cd0000ff",
                          fontWeight: "600",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#f5f5f5";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "white";
                        }}
                      >
                        <MdDelete className="icons" />
                        Delete
                      </button>
                    </div>
                  )}
                </div>
                <div className="category-toggle">
                  <Toggle
                    onChange={(newChecked) =>
                      onToggleChange(category.id, newChecked)
                    }
                    checked={checkedMap[category.id] ?? category.isActive ?? false}
                  />
                </div>
                <div className="card__image card__image--flowers">

                  <img 
                  src={imageBlobUrls[category.id] || `${process.env.REACT_APP_BASE_IMAGE}${category.image}`}
                  alt={category.title} />

                </div>
                <div className="card__content">
                  <div className="card__title">
                    <h3 style={{fontWeight:"semi-bold"}}>{category.title}</h3>
                    <p className="card__text card__text--truncate">
                      {category.description}
                    </p>
                    <button
                      onClick={() => handleOpenReadMore(category)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#0A4DA2",
                        cursor: "pointer",
                        padding: "0",
                        fontSize: "0.875rem",
                        fontWeight: "500",
                        marginTop: "8px",
                      }}
                    >
                      Read more
                    </button>
                  </div>
                </div>
              </div>
            </li>
            ))
          )}
        </ul>

        {filteredCategories.length > 0 && (totalPages > 1 || isShowingAll) && (
          <div style={{ marginTop: "16px", textAlign: "center" }}>
            <div style={{ marginBottom: "8px", fontSize: "14px", color: "#666" }}>
              {isShowingAll ? (
                <span>Showing all {totalItems} items</span>
              ) : (
                <>
                  Page <strong>{activePage}</strong> of <strong>{totalPages}</strong>
                  {totalItems ? <span> ({totalItems} total items)</span> : null}
                </>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}>
              {!isShowingAll && (
                <Pagination
                  currentPage={activePage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              )}
              <button
                className="commonButton"
                onClick={() => {
                  setIsShowingAll(!isShowingAll);
                  setCurrentPage(1);
                }}
                style={{
                  fontSize: "14px",
                  padding: "6px 16px",
                  height: "36px",
                  borderRadius: "20px",
                  marginTop: isShowingAll ? "0px" : "8px",
                }}
              >
                {isShowingAll ? "Show Less (Paginated)" : "Show All"}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
export default Categories;
